import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdForgeLogo from '../../components/feature/AdForgeLogo';
import { trackEvent } from '../../lib/analytics';

const benefits = [
  'Free plan — no credit card required',
  'Instantly generate ready-to-run ads',
  'Facebook, Instagram & Google support',
  'Upgrade or cancel anytime',
];

export default function SignUpPage() {
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [formState, setFormState] = useState({ name: '', email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field: string, val: string) => {
    setFormState((s) => ({ ...s, [field]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.name || !formState.email || !formState.password) {
      setError('Please fill in all fields.');
      return;
    }
    if (formState.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setError('');
    setIsLoading(true);
    const { error: authError, convertedAnonymous } = await signUp(formState.email, formState.password, formState.name);
    setIsLoading(false);
    if (authError) {
      setError(authError);
    } else {
      trackEvent('signup', { method: 'email', converted_anonymous: Boolean(convertedAnonymous) });
      if (convertedAnonymous) {
        window.localStorage.removeItem('adforge_quick_start_anonymous_generations');
        window.localStorage.removeItem('adforge_quick_start_anonymous_attempts');
        setSuccess('Account created! Your generated ads are saved in your history.');
        setTimeout(() => navigate('/dashboard/history'), 1200);
      } else {
        setSuccess('Account created! Check your email to confirm, then sign in.');
        setTimeout(() => navigate('/login'), 3000);
      }
    }
  };

  const handleGoogle = async () => {
    setError('');
    setIsGoogleLoading(true);
    const { error: authError } = await signInWithGoogle();
    setIsGoogleLoading(false);
    if (authError) setError(authError);
    else trackEvent('signup', { method: 'google' });
    // on success, Supabase redirects the browser automatically
  };

  return (
    <div className="min-h-screen bg-stone-50 font-sans flex">
      <div className="hidden lg:flex lg:w-5/12 bg-stone-950 flex-col justify-between p-12">
        <Link to="/">
          <AdForgeLogo size="md" light={true} />
        </Link>
        <div>
          <h2 className="text-3xl font-extrabold text-white mb-3">
            Start generating ads that
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent"> actually convert.</span>
          </h2>
          <p className="text-stone-400 text-sm mb-8 leading-relaxed">
            Join 3,200+ businesses using AdForge AI to write better ads in minutes. No copywriting skills needed.
          </p>
          <ul className="flex flex-col gap-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-center gap-3 text-sm text-stone-300">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-500/20 text-orange-400 shrink-0">
                  <i className="ri-check-line text-xs" />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-stone-500">© 2026 AdForge AI</p>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Link to="/">
              <AdForgeLogo size="lg" light={false} />
            </Link>
          </div>

          <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Create your free account</h1>
          <p className="text-sm text-stone-500 mb-8">
            Already have an account?{' '}
            <Link to="/login" className="text-orange-500 font-semibold hover:underline">Sign in</Link>
          </p>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-green-50 border border-green-100 text-green-700 text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-base" />
              {success}
            </div>
          )}

          <button
            type="button"
            onClick={handleGoogle}
            disabled={isGoogleLoading || isLoading}
            className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl border border-stone-200 bg-white text-stone-700 font-semibold text-sm hover:bg-stone-50 hover:border-stone-300 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-70 mb-5"
          >
            {isGoogleLoading ? (
              <i className="ri-loader-4-line animate-spin text-base" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
            )}
            Continue with Google
          </button>

          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-stone-50 px-3 text-xs text-stone-400">or sign up with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Full name</label>
              <input
                type="text"
                name="name"
                value={formState.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="John Smith"
                className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Email address</label>
              <input
                type="email"
                name="email"
                value={formState.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="you@example.com"
                className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={formState.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full text-sm px-4 py-3 pr-11 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
                >
                  <i className={`ri-${showPass ? 'eye-off' : 'eye'}-line text-sm`} />
                </button>
              </div>
              {formState.password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full bg-stone-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        formState.password.length < 6 ? 'w-1/4 bg-rose-400' :
                        formState.password.length < 10 ? 'w-1/2 bg-amber-400' :
                        'w-full bg-green-500'
                      }`}
                    />
                  </div>
                  <span className="text-xs text-stone-400">
                    {formState.password.length < 6 ? 'Weak' : formState.password.length < 10 ? 'Fair' : 'Strong'}
                  </span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold text-sm hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-70 mt-1"
            >
              {isLoading ? <><i className="ri-loader-4-line animate-spin" /> Creating account...</> : <>
                <i className="ri-flashlight-fill" />
                Create Free Account
              </>}
            </button>
          </form>

          <p className="mt-4 text-xs text-stone-400 text-center">
            By signing up, you agree to our{' '}
            <a href="#" className="text-orange-500 hover:underline">Terms of Service</a> and{' '}
            <a href="#" className="text-orange-500 hover:underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
